// =============================================================================
// src/features/analytics/analytics-insights-edition-parser.ts - Orquestra o
// parse do DOCUMENTO markdown de uma edicao viva (banco -> Hasura) para
// AnalyticsInsightEditionDoc. Estrategia de falha em camadas (2026-07-28):
// issues brandas degradam localmente; falha estrutural (capa/prioridade
// ausentes, zero cards, card irreconhecivel) devolve outcome 'generic' — o
// documento REAL do dia e renderizado como titulos+paragrafos+tabelas, mais
// honesto que trocar silenciosamente pela edicao estatica de outra data.
// Nota de deriva e SEMPRE 'generic' (estrutura nunca exercitada em campo:
// zero exemplares reais ate 2026-07-28; listas `- item` viram paragrafo).
// =============================================================================

import type { AnalyticsInsightParseIssue } from './analytics-insights-card-parser';
import { parseAnalyticsInsightCard } from './analytics-insights-card-parser';
import type { AnalyticsInsightMarkdownBlock } from './analytics-insights-markdown-blocks';
import { scanAnalyticsInsightMarkdownBlocks } from './analytics-insights-markdown-blocks';
import {
  analyticsInsightInlineHtml,
  analyticsInsightJoinHardWrap,
  analyticsInsightPlainText,
} from './analytics-insights-markdown-inline';
import type {
  AnalyticsInsightBlock,
  AnalyticsInsightBlockId,
  AnalyticsInsightCard,
  AnalyticsInsightDocKind,
  AnalyticsInsightEditionDoc,
  AnalyticsInsightEvidenceBlock,
} from './analytics-insights-types';

/** Secao do render generico (nota de deriva / documento fora do contrato). */
export interface AnalyticsInsightGenericSection {
  /** HTML inline do heading; null para o preambulo antes do 1o heading. */
  readonly headingHtml: string | null;
  readonly bodyBlocks: readonly AnalyticsInsightEvidenceBlock[];
}

export type AnalyticsInsightParseResult =
  | {
      readonly outcome: 'cards';
      readonly editionDoc: AnalyticsInsightEditionDoc;
      readonly parseIssues: readonly AnalyticsInsightParseIssue[];
    }
  | {
      readonly outcome: 'generic';
      readonly genericSections: readonly AnalyticsInsightGenericSection[];
      readonly parseIssues: readonly AnalyticsInsightParseIssue[];
    };

const REGEX_PRIORITY_CARD_CELL = /^CARD (\d{2}) — (.+)$/;
const COVER_TABLE_FIRST_HEADER = 'Corte';
const PRIORITY_HEADING_PREFIX = 'Prioridade da edição';
const BLOCK_HEADING_PREFIX = 'Bloco ';

/** Entrada unica do parse de documento; docKind decide a rota. */
export function parseAnalyticsInsightEditionMarkdown(
  markdownBody: string,
  docKind: AnalyticsInsightDocKind,
): AnalyticsInsightParseResult {
  const scannedBlocks = scanAnalyticsInsightMarkdownBlocks(markdownBody);
  if (docKind === 'nota_de_deriva') {
    return {
      outcome: 'generic',
      genericSections: analyticsInsightGenericSectionsFromBlocks(scannedBlocks),
      parseIssues: [],
    };
  }
  const issues: AnalyticsInsightParseIssue[] = [];
  const editionDoc = parseAnalyticsInsightCardsDocument(scannedBlocks, issues);
  if (editionDoc !== null) return { outcome: 'cards', editionDoc, parseIssues: issues };
  return {
    outcome: 'generic',
    genericSections: analyticsInsightGenericSectionsFromBlocks(scannedBlocks),
    parseIssues: issues,
  };
}

/** Render generico direto de um markdown (usado tambem pelos fallbacks). */
export function parseAnalyticsInsightGenericSections(
  markdownBody: string,
): readonly AnalyticsInsightGenericSection[] {
  return analyticsInsightGenericSectionsFromBlocks(scanAnalyticsInsightMarkdownBlocks(markdownBody));
}

// -------------------------------------------------------------- documento ---

function parseAnalyticsInsightCardsDocument(
  scannedBlocks: readonly AnalyticsInsightMarkdownBlock[],
  issues: AnalyticsInsightParseIssue[],
): AnalyticsInsightEditionDoc | null {
  const priorityHeadingIndex = scannedBlocks.findIndex(
    (markdownBlock) =>
      markdownBlock.mdKind === 'heading' &&
      markdownBlock.headingLevel === 2 &&
      markdownBlock.headingText.startsWith(PRIORITY_HEADING_PREFIX),
  );
  if (priorityHeadingIndex < 0) {
    issues.push({ issueCode: 'prioridade_ausente', detail: `sem heading "${PRIORITY_HEADING_PREFIX}"` });
    return null;
  }
  const cover = parseAnalyticsInsightCover(scannedBlocks.slice(0, priorityHeadingIndex), issues);
  const priorityTitleByCardNumber = parseAnalyticsInsightPriorityMap(
    scannedBlocks.slice(priorityHeadingIndex + 1), issues,
  );
  const editionBlocks = parseAnalyticsInsightCardBlocks(
    scannedBlocks.slice(priorityHeadingIndex + 1), priorityTitleByCardNumber, issues,
  );
  const documentFooter = parseAnalyticsInsightDocumentFooter(scannedBlocks, issues);
  if (cover === null || priorityTitleByCardNumber === null || editionBlocks === null) return null;
  return { edition: { ...cover, documentFooter }, blocks: editionBlocks };
}

// ------------------------------------------------------------------- capa ---

interface AnalyticsInsightParsedCover {
  readonly monitoredHtml: string;
  readonly cutLabel: string;
  readonly analyticalWindowLabel: string;
  readonly extractionLabel: string;
  readonly baseVolumesLabel: string;
  readonly cardCountLabel: string;
  readonly coverNotesHtml: readonly string[];
}

function parseAnalyticsInsightCover(
  coverBlocks: readonly AnalyticsInsightMarkdownBlock[],
  issues: AnalyticsInsightParseIssue[],
): AnalyticsInsightParsedCover | null {
  const coverTableIndex = coverBlocks.findIndex(
    (markdownBlock) =>
      markdownBlock.mdKind === 'table' &&
      /* v8 ignore next -- splitAnalyticsInsightTableRow sempre devolve >=1 celula; ?? '' e defensivo (noUncheckedIndexedAccess) */
      analyticsInsightPlainText(markdownBlock.headerCells[0] ?? '') === COVER_TABLE_FIRST_HEADER,
  );
  const coverTable = coverBlocks[coverTableIndex];
  const factCells = coverTable?.mdKind === 'table' ? coverTable.rowsCells[0] : undefined;
  if (factCells === undefined || factCells.length < 5) {
    issues.push({ issueCode: 'capa_ausente', detail: `tabela "${COVER_TABLE_FIRST_HEADER}" nao encontrada` });
    return null;
  }
  /* v8 ignore next -- length >= 5 acabou de ser validado; ?? '' e defensivo (noUncheckedIndexedAccess) */
  const factCell = (cellIndex: number): string => analyticsInsightPlainText(factCells[cellIndex] ?? '');
  return {
    monitoredHtml: parseAnalyticsInsightMonitoredLine(coverBlocks, issues),
    cutLabel: factCell(0),
    analyticalWindowLabel: factCell(1),
    extractionLabel: factCell(2),
    baseVolumesLabel: factCell(3),
    cardCountLabel: factCell(4),
    coverNotesHtml: coverBlocks
      .slice(coverTableIndex + 1)
      .filter((markdownBlock) => markdownBlock.mdKind === 'paragraph')
      .map((noteBlock) => analyticsInsightInlineHtml(analyticsInsightJoinHardWrap(noteBlock.paragraphLines))),
  };
}

function parseAnalyticsInsightMonitoredLine(
  coverBlocks: readonly AnalyticsInsightMarkdownBlock[],
  issues: AnalyticsInsightParseIssue[],
): string {
  for (const markdownBlock of coverBlocks) {
    if (markdownBlock.mdKind !== 'paragraph') continue;
    const joinedParagraph = analyticsInsightJoinHardWrap(markdownBlock.paragraphLines);
    if (joinedParagraph.startsWith('Monitorado:')) return analyticsInsightInlineHtml(joinedParagraph);
  }
  issues.push({ issueCode: 'monitorado_ausente', detail: 'linha "Monitorado:" nao encontrada' });
  return '';
}

// ------------------------------------------------------------- prioridade ---

function parseAnalyticsInsightPriorityMap(
  blocksAfterPriorityHeading: readonly AnalyticsInsightMarkdownBlock[],
  issues: AnalyticsInsightParseIssue[],
): ReadonlyMap<number, string> | null {
  // A busca PARA no primeiro separador/heading: sem isso, uma edicao sem a
  // tabela de prioridade "adotaria" a primeira tabela de evidencia de um card
  // (bug pego pelo teste de tabela ausente em 2026-07-28).
  let priorityTable: Extract<AnalyticsInsightMarkdownBlock, { mdKind: 'table' }> | null = null;
  for (const markdownBlock of blocksAfterPriorityHeading) {
    if (markdownBlock.mdKind === 'separator' || markdownBlock.mdKind === 'heading') break;
    if (markdownBlock.mdKind === 'table') {
      priorityTable = markdownBlock;
      break;
    }
  }
  if (priorityTable === null || priorityTable.rowsCells.length === 0) {
    issues.push({ issueCode: 'tabela_prioridade_ausente', detail: 'sem tabela apos o heading de prioridade' });
    return null;
  }
  const priorityTitleByCardNumber = new Map<number, string>();
  for (const rowCells of priorityTable.rowsCells) {
    const cardCell = analyticsInsightPlainText(rowCells[1] ?? '');
    const cardCellMatch = REGEX_PRIORITY_CARD_CELL.exec(cardCell);
    if (cardCellMatch?.[1] === undefined || cardCellMatch[2] === undefined) {
      issues.push({ issueCode: 'linha_prioridade_invalida', detail: cardCell });
      continue;
    }
    priorityTitleByCardNumber.set(Number(cardCellMatch[1]), cardCellMatch[2]);
  }
  return priorityTitleByCardNumber;
}

// ------------------------------------------------------------------ cards ---

function parseAnalyticsInsightCardBlocks(
  blocksAfterPriorityHeading: readonly AnalyticsInsightMarkdownBlock[],
  priorityTitleByCardNumber: ReadonlyMap<number, string> | null,
  issues: AnalyticsInsightParseIssue[],
): readonly AnalyticsInsightBlock[] | null {
  const editionBlocks: { blockId: AnalyticsInsightBlockId; blockTitle: string; blockCards: AnalyticsInsightCard[] }[] = [];
  let cardFailed = false;
  for (const cardGroup of groupAnalyticsInsightCardSections(blocksAfterPriorityHeading)) {
    if (cardGroup.groupKind === 'block') {
      const blockId = analyticsInsightBlockIdFromHeading(cardGroup.headingText, issues);
      if (blockId === null) return null;
      editionBlocks.push({ blockId, blockTitle: analyticsInsightPlainText(cardGroup.headingText), blockCards: [] });
      continue;
    }
    const outcome = parseAnalyticsInsightCard(
      cardGroup.headingText, cardGroup.cardBlocks, priorityTitleByCardNumber ?? new Map(),
    );
    issues.push(...outcome.issues);
    if (outcome.card === null) cardFailed = true;
    else editionBlocks[editionBlocks.length - 1]?.blockCards.push(outcome.card);
  }
  const totalCards = editionBlocks.reduce((cardCount, editionBlock) => cardCount + editionBlock.blockCards.length, 0);
  if (cardFailed || totalCards === 0) {
    if (totalCards === 0) issues.push({ issueCode: 'zero_cards', detail: 'nenhum card parseado' });
    return null;
  }
  return editionBlocks;
}

/** Agrupa o fluxo pos-prioridade em headings de bloco e grupos de card.
 * O separador `---` FECHA o card corrente — sem isso o paragrafo final
 * *ATALAIA...* (rodape do documento) vazaria p/ dentro do ultimo card. */
function groupAnalyticsInsightCardSections(
  blocksAfterPriorityHeading: readonly AnalyticsInsightMarkdownBlock[],
): readonly (
  | { readonly groupKind: 'block'; readonly headingText: string }
  | { readonly groupKind: 'card'; readonly headingText: string; readonly cardBlocks: AnalyticsInsightMarkdownBlock[] }
)[] {
  const cardGroups: (
    | { groupKind: 'block'; headingText: string }
    | { groupKind: 'card'; headingText: string; cardBlocks: AnalyticsInsightMarkdownBlock[] }
  )[] = [];
  let openCard: { cardBlocks: AnalyticsInsightMarkdownBlock[] } | null = null;
  for (const markdownBlock of blocksAfterPriorityHeading) {
    if (markdownBlock.mdKind === 'separator') {
      openCard = null;
      continue;
    }
    if (markdownBlock.mdKind === 'heading') {
      openCard = null;
      if (markdownBlock.headingLevel === 2 && markdownBlock.headingText.startsWith(BLOCK_HEADING_PREFIX)) {
        cardGroups.push({ groupKind: 'block', headingText: markdownBlock.headingText });
      } else if (markdownBlock.headingLevel === 3) {
        const cardGroup = { groupKind: 'card' as const, headingText: markdownBlock.headingText, cardBlocks: [] };
        cardGroups.push(cardGroup);
        openCard = cardGroup;
      }
      continue;
    }
    openCard?.cardBlocks.push(markdownBlock);
  }
  return cardGroups;
}

function analyticsInsightBlockIdFromHeading(
  blockHeadingText: string,
  issues: AnalyticsInsightParseIssue[],
): AnalyticsInsightBlockId | null {
  const blockLetter = blockHeadingText.charAt(BLOCK_HEADING_PREFIX.length);
  if (blockLetter === 'A' || blockLetter === 'B' || blockLetter === 'C' || blockLetter === 'D') {
    return blockLetter;
  }
  issues.push({ issueCode: 'bloco_desconhecido', detail: blockHeadingText });
  return null;
}

// ----------------------------------------------------- rodape do documento ---

function parseAnalyticsInsightDocumentFooter(
  scannedBlocks: readonly AnalyticsInsightMarkdownBlock[],
  issues: AnalyticsInsightParseIssue[],
): string {
  for (let blockIndex = scannedBlocks.length - 1; blockIndex >= 0; blockIndex -= 1) {
    const markdownBlock = scannedBlocks[blockIndex];
    /* v8 ignore next -- blockIndex e valido por construcao do for; ?. e defensivo (noUncheckedIndexedAccess) */
    if (markdownBlock?.mdKind !== 'paragraph') continue;
    const joinedParagraph = analyticsInsightJoinHardWrap(markdownBlock.paragraphLines);
    if (joinedParagraph.startsWith('*ATALAIA')) return analyticsInsightPlainText(joinedParagraph);
    break;
  }
  issues.push({ issueCode: 'rodape_documento_ausente', detail: 'paragrafo final *ATALAIA...* nao encontrado' });
  return '';
}

// ---------------------------------------------------------------- generico ---

/** Cards -> nao; aqui todo bloco vira secao legivel (headings agrupam). */
function analyticsInsightGenericSectionsFromBlocks(
  scannedBlocks: readonly AnalyticsInsightMarkdownBlock[],
): readonly AnalyticsInsightGenericSection[] {
  const genericSections: { headingHtml: string | null; bodyBlocks: AnalyticsInsightEvidenceBlock[] }[] = [];
  for (const markdownBlock of scannedBlocks) {
    if (markdownBlock.mdKind === 'separator') continue;
    if (markdownBlock.mdKind === 'heading') {
      genericSections.push({ headingHtml: analyticsInsightInlineHtml(markdownBlock.headingText), bodyBlocks: [] });
      continue;
    }
    if (genericSections.length === 0) genericSections.push({ headingHtml: null, bodyBlocks: [] });
    genericSections[genericSections.length - 1]?.bodyBlocks.push(analyticsInsightGenericBodyBlock(markdownBlock));
  }
  // Sem filtro de secao vazia: a secao de preambulo (heading null) so nasce
  // quando ja existe um body block a inserir — nunca fica vazia.
  return genericSections;
}

/** O tipo exclui heading/separator: o chamador ja os tratou (narrowing). */
function analyticsInsightGenericBodyBlock(
  markdownBlock: Extract<AnalyticsInsightMarkdownBlock, { mdKind: 'paragraph' | 'table' | 'blockquote' }>,
): AnalyticsInsightEvidenceBlock {
  if (markdownBlock.mdKind === 'table') {
    return {
      blockKind: 'table',
      columnHeaders: markdownBlock.headerCells.map(analyticsInsightPlainText),
      tableRows: markdownBlock.rowsCells.map((rowCells) => rowCells.map(analyticsInsightInlineHtml)),
    };
  }
  const paragraphLines =
    markdownBlock.mdKind === 'blockquote' ? markdownBlock.quoteLines : markdownBlock.paragraphLines;
  return {
    blockKind: 'paragraph',
    paragraphHtml: analyticsInsightInlineHtml(analyticsInsightJoinHardWrap(paragraphLines)),
  };
}
